import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Navbar } from 'react-bootstrap';
import { createSelector } from '@reduxjs/toolkit';

import routes from '@/config/routes';
import { NAVBAR_HEIGHT, Z_INDEX_NAV_DROPDOWN } from '@/config/variables';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { MarketplaceLogo } from '@/assets/images';
import { otherColors } from '@/styles/colors';
import { sizing } from '@/styles/theme';
import { UserType } from '@/store/enums';
import { selectAuthMenuType, selectUser, selectUserType } from '@/store/selectors/authSelectors';
import { getMainPage } from '@/components/layout/routes';
import { classList } from '@/helpers/other';
import { AuthedNavigation } from '@/components/layout/navigation';
import { getFeatureFlags } from '@/store/slices/featureFlags/featureFlagsSlice';
import { LayoutContainerStyled } from '../layout-container/styles';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@/breakpoints-config/breakpoints';

const navigationSelector = createSelector(
  selectUser,
  selectAuthMenuType,
  selectUserType,
  (user, menuType, userType) => ({
    user,
    menuType,
    userType,
  }),
);

const Navigation = (): React.ReactElement => {
  const { user, menuType, userType } = useAppSelector(navigationSelector);
  const location = useLocation();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const query = new URLSearchParams(search);
  const resumeBuilderStep = query.get('step');
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: breakpoints.small });

  const onLogoClicked = () => {
    if (menuType !== UserType.client) history.push(getMainPage(menuType));
  };

  useEffect(() => {
    dispatch(getFeatureFlags());
  }, [dispatch]);

  const NavComponent = ({ children }: { children: React.ReactElement }) => {
    if (!isMobile) {
      return <LayoutContainerStyled className="navbar">{children}</LayoutContainerStyled>;
    } else {
      return <Fragment>{children}</Fragment>;
    }
  };

  return (
    <StyledNavbar
      expanded={isExpanded}
      bg="white"
      expand="sm"
      className={classList(
        location.pathname === routes.sp.onboarding.resumeBuilder && !resumeBuilderStep && 'd-none d-md-flex',
        'px-0',
        (location.pathname === routes.sp.onboarding.gigs || location.pathname.indexOf(routes.admin.gig.preview) > -1) &&
          'fixed-header',
      )}
    >
      <NavComponent>
        <>
          <Navbar.Brand>
            <ImageTag src={MarketplaceLogo} onClick={onLogoClicked} />
          </Navbar.Brand>
          {user && (
            <AuthedNavigation
              user={user}
              menuType={menuType}
              userType={userType}
              toggleExpand={() => setIsExpanded(prevState => !prevState)}
              onCollapse={() => setIsExpanded(false)}
            />
          )}
        </>
      </NavComponent>
    </StyledNavbar>
  );
};

export { Navigation };

const StyledNavbar = styled(Navbar)`
  border-bottom: 1px solid ${otherColors.border.default};
  height: ${NAVBAR_HEIGHT}px;
  z-index: ${Z_INDEX_NAV_DROPDOWN};

  &.fixed-header {
    position: fixed;
    width: 100%;
    @media (max-width: 768px) {
      position: unset;
    }
  }

  .nav-link,
  .dropdown-item {
    font-family: 'Barlow';
    font-size: ${sizing.fontSize.base};
    font-weight: 600;
    text-decoration: none;
    display: flex;
    align-items: center;
    color: #135188 !important;
  }

  .collapse {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding-bottom: 16px;

    /* apply to default media queries for Bootstrap navbar */
    @media (min-width: 576px) {
      box-shadow: none;
      padding-bottom: 0;
    }
  }

  .nav {
    &-link {
      margin-left: 1.5rem;
      &.active {
        font-weight: 700;
        color: #135188 !important;
      }
    }

    &bar-brand {
      margin-left: 16px;
    }

    &bar-toggler {
      margin-right: 16px;
      width: 56px;
    }
  }

  .dropdown {
    display: flex;
    justify-content: center;

    &-item {
      &.active,
      &:visited {
        background-color: #e9ecef;
      }
    }
  }
`;

const ImageTag = styled.img`
  cursor: pointer;
`;
